import React from 'react';

import styled from 'styled-components';

import { Calories } from 'components/price-and-calories';
import useLocation from 'hooks/location/use-location';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMainMenuContext } from 'state/main-menu';
import { useMenuContext } from 'state/menu';
import { EventName, emitEvent } from 'utils/event-hub';
import { routes } from 'utils/routing';

import {
  ItemPrice,
  ListTileImage,
  PriceAndCaloriesListWrapper,
  TileImage,
  TileInner,
  tileHeaderStyles,
} from './styled';
import { IMenuTileProps, IPriceAndCaloriesProps } from './types';

const ItemCalories = styled.p`
  color: ${Styles.color.grey.two};
  margin-bottom: 0;
`;

const TileDescription = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0.8rem 0.5rem;
  text-align: center;
  overflow: ellipsis;

  ${Styles.desktop`
    padding: 1.3rem 2rem;
  `}

  p {
    color: ${Styles.color.grey.two};
    font-family: ${Styles.fontFamily.body};
    margin-top: 0.25rem;
    font-size: 0.75rem;
  }
`;

const TileHeader = styled.span`
  ${tileHeaderStyles}
  font-weight: ${Styles.fontWeight.normal};
  letter-spacing: 'normal';
`;

const TileListDescription = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-top: 0.8rem;
  padding-bottom: 0.8rem;
  padding-left: 0.5rem;
  text-align: center;
  overflow: ellipsis;
  width: calc(100% - 7rem);

  ${Styles.desktop`
    width: unset;
    padding: 1.3rem 2rem;
  `}

  p {
    color: ${Styles.color.grey.two};
    font-family: ${Styles.fontFamily.body};
    margin-top: 0;
    margin-bottom: 0;
    font-size: 0.9375rem;

    ${Styles.desktop`
      font-size: 0.75rem;
      margin-top: 0.25rem;
      margin-bottom: unset;
    `}
  }
`;

const TileListHeader = styled.h2`
  ${tileHeaderStyles}
  font-weight: ${Styles.fontWeight.normal};
  letter-spacing: 'normal';
  font-size: 1.25rem;
  line-height: 1.2rem;
  text-align: left;

  ${Styles.desktop`
    text-align: unset;
  `}
`;

const StyledTileInner = styled(TileInner)`
  display: flex;
  align-items: center;
  padding: 0.25rem;

  ${Styles.desktop`
    padding: 0;
    display: block;
  `}
`;

const StyledPriceAndCaloriesListWrapper = styled(PriceAndCaloriesListWrapper)`
  margin-top: 0.3rem;
`;

const StyledItemPrice = styled.span`
  font-size: 0.9375rem;
  font-family: ${Styles.fontFamily.body};
  color: ${Styles.color.pricing};
  margin-right: 0.3rem;

  ${Styles.desktop`
    margin-right: 0;
    font-weight: ${Styles.fontWeight.heavy};
    font-size: 1rem;
    font-family: ${Styles.fontFamily.base};
  `}
`;

const priceAndCalories = ({
  calories,
  price,
  showStaticMenu,
  type,
}: IPriceAndCaloriesProps & {
  showStaticMenu: boolean;
}): JSX.Element | null => {
  if (type === 'section') {
    return null;
  }

  return (
    <>
      {!showStaticMenu && <ItemPrice>{price}</ItemPrice>}
      <Calories>
        <ItemCalories>{calories}</ItemCalories>
      </Calories>
    </>
  );
};

const priceAndCaloriesList = ({
  calories,
  price,
  showStaticMenu,
  type,
}: IPriceAndCaloriesProps & {
  showStaticMenu: boolean;
}): JSX.Element | null => {
  const shouldRenderPriceAndCalories = (!showStaticMenu && price) || calories;
  if (type === 'section' || !shouldRenderPriceAndCalories) {
    return null;
  }

  return (
    <StyledPriceAndCaloriesListWrapper>
      {!showStaticMenu && <StyledItemPrice>{price}</StyledItemPrice>}
      <Calories>
        <ItemCalories>{calories}</ItemCalories>
      </Calories>
    </StyledPriceAndCaloriesListWrapper>
  );
};

const MenuTile = React.memo(
  ({
    enableMenuList,
    image,
    objectFitContain,
    calories,
    name,
    type,
    price,
    id,
  }: IMenuTileProps) => {
    const enablePriceAndCaloriesInMenu = useFlag(
      LaunchDarklyFlag.ENABLE_PRICE_AND_CALORIES_IN_MENU
    );
    const {
      location: { pathname },
    } = useLocation();

    const { showStaticMenu } = useMenuContext();
    const { checkIfUserIsOnMainMenuPath } = useMainMenuContext();

    const [Inner, Image, Description, Header] = enableMenuList
      ? [StyledTileInner, ListTileImage, TileListDescription, TileListHeader]
      : [TileInner, TileImage, TileDescription, TileHeader];

    const priceAndCaloriesContent = enableMenuList
      ? priceAndCaloriesList({ type, price, calories, showStaticMenu })
      : priceAndCalories({ type, price, calories, showStaticMenu });

    const handleOnImageLoaded = () => {
      emitEvent(EventName.MENU_TILE_IMAGE_LOADED, {
        sanityId: id,
        isOnMainMenu: pathname === routes.menu,
        attributes: [],
      });
    };

    return (
      <Inner data-testid="menu-tile-bk">
        <Image
          image={image}
          alt=""
          objectFitContain={objectFitContain}
          $isOnMainMenu={checkIfUserIsOnMainMenuPath()}
          onImageLoaded={handleOnImageLoaded}
        />
        <Description>
          <Header
            data-testid={`tile-header-${(name || '').toLowerCase().replace(/\s/g, '')}`}
            id={id}
          >
            {name}
          </Header>
          {enablePriceAndCaloriesInMenu && priceAndCaloriesContent}
        </Description>
      </Inner>
    );
  }
);

export default MenuTile;
